/**
 * Image import & decoding (WebKit-tuned).
 *  • `createImageBitmap(file, {imageOrientation: 'from-image'})` applies EXIF rotation
 *  • images larger than the iOS canvas ceiling are downscaled once at import
 *  • large PNGs are re-encoded as high-quality JPEG to respect storage budgets
 *  • thumbnails are JPEG, ≤ 480 px on the long side
 */

/** iOS/iPadOS 18+ allow 8192×8192 per canvas; stay comfortably below. */
export const MAX_CANVAS_AREA = 8192 * 8192 * 0.85;
export const MAX_SIDE = 8192;
/** Originals above this size are stored as JPEG (q 0.92) instead of PNG. */
export const MAX_PNG_BYTES = 3_000_000;

export interface DecodedImage {
  bitmap: ImageBitmap;
  w: number;
  h: number;
  blob: Blob; // bytes to persist (original, or re-encoded)
  type: string; // image/png | image/jpeg
}

export function isImageFile(f: File | Blob): boolean {
  return /^image\/(png|jpeg|jpg|webp|heic|heif|gif|bmp|tiff?)$/i.test(f.type) || (f instanceof File && /\.(png|jpe?g|webp|heic|heif|gif|bmp|tiff?)$/i.test(f.name));
}

/** Pull image files out of a paste / drop payload. */
export function imageFilesFrom(dt: DataTransfer | null | undefined): File[] {
  if (!dt) return [];
  const out: File[] = [];
  if (dt.items) {
    for (const it of Array.from(dt.items)) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f && isImageFile(f)) out.push(f);
      }
    }
  }
  if (out.length === 0 && dt.files) for (const f of Array.from(dt.files)) if (isImageFile(f)) out.push(f);
  return out;
}

async function bitmapFrom(blob: Blob, resize?: { w: number; h: number }): Promise<ImageBitmap> {
  const opts: ImageBitmapOptions = { imageOrientation: 'from-image', premultiplyAlpha: 'default' };
  if (resize) {
    opts.resizeWidth = resize.w;
    opts.resizeHeight = resize.h;
    opts.resizeQuality = 'high';
  }
  try {
    return await createImageBitmap(blob, opts);
  } catch {
    // Fallback: decode through an <img> (unusual formats).
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      await img.decode();
      return await createImageBitmap(img, opts);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality);
  });
}

/** Free a scratch canvas's backing store (WebKit counts total canvas memory). */
export function releaseCanvas(c: HTMLCanvasElement): void {
  c.width = 1;
  c.height = 1;
}

async function reencode(bmp: ImageBitmap, type: string, quality?: number): Promise<Blob> {
  const c = document.createElement('canvas');
  c.width = bmp.width;
  c.height = bmp.height;
  const ctx = c.getContext('2d')!;
  if (type === 'image/jpeg') {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
  }
  ctx.drawImage(bmp, 0, 0);
  const blob = await canvasToBlob(c, type, quality);
  releaseCanvas(c);
  return blob;
}

export async function decodeImage(file: Blob): Promise<DecodedImage> {
  let bmp = await bitmapFrom(file);
  let blob = file;
  let type = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
  const area = bmp.width * bmp.height;
  const side = Math.max(bmp.width, bmp.height);
  if (area > MAX_CANVAS_AREA || side > MAX_SIDE) {
    const k = Math.min(Math.sqrt(MAX_CANVAS_AREA / area), MAX_SIDE / side);
    const w = Math.max(1, Math.floor(bmp.width * k));
    const h = Math.max(1, Math.floor(bmp.height * k));
    bmp.close();
    bmp = await bitmapFrom(file, { w, h });
    type = 'image/jpeg';
    blob = await reencode(bmp, type, 0.92);
  } else if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
    // Normalise exotic formats (webp/heic/gif...) so every engine can decode later.
    type = 'image/png';
    blob = await reencode(bmp, type);
  }
  if (type === 'image/png' && blob.size > MAX_PNG_BYTES) {
    type = 'image/jpeg';
    blob = await reencode(bmp, type, 0.92);
  }
  return { bitmap: bmp, w: bmp.width, h: bmp.height, blob, type };
}

export async function makeThumbnail(bitmap: ImageBitmap, maxSide = 480): Promise<Blob> {
  const k = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * k));
  const h = Math.max(1, Math.round(bitmap.height * k));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  const blob = await canvasToBlob(c, 'image/jpeg', 0.82);
  releaseCanvas(c);
  return blob;
}

/** Open the native photo picker. Resolves with the chosen files (empty if cancelled). */
export function pickImages(multiple = false): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*'; // never list image/heic: Safari would convert JPEG/PNG picks to HEIC
    input.multiple = multiple;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';
    document.body.append(input);
    const done = (files: File[]) => {
      input.remove();
      resolve(files);
    };
    input.addEventListener('change', () => done(Array.from(input.files ?? []).filter(isImageFile)), { once: true });
    input.addEventListener('cancel', () => done([]), { once: true });
    input.click();
  });
}
